import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  formatClock,
  formatGb,
  formatUsdCost,
  formatUsdPerMonth,
  useStorageBreakdownQuery,
} from "@/partner/entities/storage";
import { useIsBunnyProvider } from "@/partner/entities/platforms";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants/routes";
import { ErrorMessage } from "@/shared/components";
import { useIsMobile } from "@/shared/hooks";
import { formatNumber } from "@/shared/utils/formatNumber";
import {
  StorageBreakdownSortType,
  type StorageBreakdownQueryType,
} from "@/types/graphql/partner.generated";
import { DeleteStorageDialog } from "./DeleteStorageDialog";

export type StorageItem =
  StorageBreakdownQueryType["storageBreakdown"]["items"][number];

// CRITICAL-RULES.md — i18n: ключ только статический литерал, выбор через
// явную мапу. «≈» перед числом означает, что размер оценён по длительности.
const SIZE_TEXT_KEYS = {
  exact: "storage.list.sizeValue",
  estimated: "storage.list.sizeEstimated",
} as const;

/**
 * Список видео по стоимости хранения: Card + MUI Table как у остальных
 * списков кабинета (Payments, Promocodes), с выделением строк и панелью
 * действий над выбранными (образец: PromocodesList). Длительность и
 * просмотренное время в «чч:мм:сс».
 */
export const StorageList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile("md");
  // Bunny тарифицирует объём, Cloudflare минуты: у Bunny в таблице есть
  // колонка размера, а цена приходит уже посчитанной по нужному тарифу.
  const isBunny = useIsBunnyProvider();
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const sort =
    (filter.sort as StorageBreakdownSortType | undefined) ??
    StorageBreakdownSortType.StorageCostDescType;
  const onlyUnwatched = Boolean(filter.onlyUnwatched);

  const { data, loading, error } = useStorageBreakdownQuery({
    variables: { page, rowsPerPage, sort, onlyUnwatched },
    fetchPolicy: "cache-and-network",
  });

  const items = data?.storageBreakdown?.items ?? [];
  const count = data?.storageBreakdown?.totalCount ?? 0;

  // Как в PromocodesList: selected это {contentId: true}, «выбрать всё»
  // работает тумблером в пределах текущей страницы.
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const selectedItems = items.filter((item) => selected[item.contentId]);
  const numSelected = selectedItems.length;

  const [pendingDeletion, setPendingDeletion] = useState<StorageItem[] | null>(
    null
  );

  const handleToggleSelectAll = () => {
    if (numSelected === items.length) {
      setSelected({});
    } else {
      setSelected(
        Object.fromEntries(items.map((item) => [item.contentId, true] as const))
      );
    }
  };

  const handleToggleSelectOne = (contentId: string) => {
    setSelected((prev) => ({ ...prev, [contentId]: !prev[contentId] }));
  };

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("storage.empty")} />;
  }

  return (
    <>
      <Card elevation={0} sx={{ overflowX: "auto" }}>
        <Collapse in={numSelected > 0}>
          <AppBar position="static" color="default" elevation={0}>
            <Toolbar>
              <Typography sx={{ mr: 2 }}>
                {t("storage.list.selected", { count: numSelected })}
              </Typography>
              <Divider
                orientation="vertical"
                variant="middle"
                sx={{ height: "2em" }}
              />
              <Button
                sx={{ ml: 2 }}
                color="inherit"
                onClick={() => setPendingDeletion(selectedItems)}
              >
                {t("storage.delete.submit")}
              </Button>
              <Divider
                orientation="vertical"
                variant="middle"
                sx={{ height: "2em" }}
              />
              <Tooltip title={t("storage.list.close")}>
                <IconButton
                  sx={{ ml: "auto" }}
                  edge="end"
                  size="large"
                  onClick={() => setSelected({})}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </AppBar>
          <Divider />
        </Collapse>

        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="default"
                  indeterminate={numSelected > 0 && numSelected < items.length}
                  checked={items.length > 0 && numSelected === items.length}
                  onChange={handleToggleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: "auto" }}>
                {t("storage.list.titleColumn")}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {t("storage.list.durationColumn")}
              </TableCell>
              {isBunny && (
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {t("storage.list.sizeColumn")}
                </TableCell>
              )}
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {t("storage.list.costColumn")}
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {t("storage.list.viewsColumn")}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {t("storage.list.watchedColumn")}
                  </TableCell>
                  <TableCell>{t("storage.list.statusColumn")}</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.contentId}
                hover
                selected={Boolean(selected[item.contentId])}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    color="default"
                    checked={Boolean(selected[item.contentId])}
                    onChange={() => handleToggleSelectOne(item.contentId)}
                  />
                </TableCell>
                <TableCell>
                  <ItemTitleLink
                    to={`${PagePaths.videos}?tab=edit&id=${item.contentId}`}
                    onOpen={() =>
                      navigate(
                        `${PagePaths.videos}?tab=edit&id=${item.contentId}`
                      )
                    }
                    color="textPrimary"
                  >
                    {item.title}
                  </ItemTitleLink>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" noWrap>
                    {formatClock(item.durationSeconds)}
                  </Typography>
                </TableCell>
                {isBunny && (
                  <TableCell align="right">
                    {item.storageBytes == null ? (
                      // Размер неизвестен: объём библиотеки не пришёл.
                      // Явный текст вместо прочерка, чтобы строка не
                      // читалась как «ничего не занимает».
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {t("storage.list.sizeUnavailable")}
                      </Typography>
                    ) : (
                      <Tooltip
                        title={
                          item.estimated
                            ? t("storage.list.sizeEstimatedHint")
                            : ""
                        }
                      >
                        <Typography variant="body2" noWrap>
                          {t(
                            item.estimated
                              ? SIZE_TEXT_KEYS.estimated
                              : SIZE_TEXT_KEYS.exact,
                            { size: formatGb(item.storageBytes) }
                          )}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
                <TableCell align="right">
                  {isBunny && item.storageBytes == null ? (
                    // Цена считается по объёму, а объёма нет: $0.00 здесь
                    // был бы неправдой.
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {t("storage.list.sizeUnavailable")}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {isBunny
                          ? formatUsdCost(item.storageCostUsdMonth)
                          : formatUsdPerMonth(item.storageCostUsdMonth)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        noWrap
                      >
                        {t("storage.list.perMonth")}
                      </Typography>
                    </>
                  )}
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell align="right">
                      <Typography variant="body2" noWrap>
                        {formatNumber(item.viewsLast90Days)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" noWrap>
                        {formatClock(item.watchedMinutesLast90Days * 60)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {item.onSale && (
                          <Chip
                            size="small"
                            color="primary"
                            label={t("storage.badge.onSale")}
                          />
                        )}
                        {item.hasPurchases && (
                          <Chip
                            size="small"
                            color="success"
                            label={t("storage.badge.hasPurchases")}
                          />
                        )}
                        {!item.onSale && !item.hasPurchases && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            noWrap
                          >
                            {t("storage.badge.notOnSale")}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 100]}
          labelRowsPerPage={t("common.pagination.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            t("common.pagination.displayedRows", {
              from,
              to,
              count:
                count !== -1 ? count : t("common.pagination.moreThan", { to }),
            })
          }
        />
      </Card>

      <DeleteStorageDialog
        items={pendingDeletion}
        onClose={() => setPendingDeletion(null)}
        onDeleted={() => {
          setPendingDeletion(null);
          setSelected({});
        }}
      />
    </>
  );
};
